class RegionRestriction extends CustomHTMLElement {
  constructor() {
    super();
    this.addToCartButtons = [];
    this.messageContainer = null;
    this.hideCheckbox = null;
    this.collectionProductCard = null;
    this.variantRestrictionsMap = {};
  }

  connectedCallback() {
    // 1) parse variant→restrictions map
    try {
      this.variantRestrictionsMap = JSON.parse(
        this.dataset.variantRestrictions || "{}"
      );
    } catch (err) {
      console.warn("Invalid JSON in data-variant-restrictions", err);
      this.variantRestrictionsMap = {};
    }

    // 2) find all Add-to-Cart buttons
    const internal = Array.from(
      this.querySelectorAll(
        'button[is="loader-button"], button.quick-buy-add, button[is="toggle-button"]'
      )
    );
    const externalSelector = this.getAttribute("button-selector");
    const external = externalSelector
      ? Array.from(document.querySelectorAll(externalSelector))
      : [];
    this.addToCartButtons = internal.concat(external);
    if (!this.addToCartButtons.length) {
      console.warn("RegionRestriction: No Add to Cart buttons found.");
    }

    // 3) insert or reuse message container
    const existing = this.querySelector(".message-container");
    if (existing) {
      this.messageContainer = existing;
    } else {
      this.messageContainer = document.createElement("div");
      this.messageContainer.classList.add("message-container");
      const lastBtn = this.addToCartButtons[this.addToCartButtons.length - 1];
      if (this.messageSelector) {
        this.querySelector(this.messageSelector).appendChild(this.messageContainer);
      } else {
        (lastBtn || this).insertAdjacentElement("beforebegin", this.messageContainer);
      }
    }

    // 4) restore “hide unavailable” checkbox
    this.hideCheckbox = document.getElementById("hide-unavailable-checkbox");
    if (this.hideCheckbox) {
      const saved = localStorage.getItem("hideUnavailable");
      if (saved !== null) this.hideCheckbox.checked = saved === "true";
      this.hideCheckbox.addEventListener("change", () => {
        localStorage.setItem("hideUnavailable", this.hideCheckbox.checked);
        document.querySelectorAll("region-restriction")
                .forEach(el => el.checkRegionRestriction());
      });
    }

    // 5) variant listeners + mutation observer
    this.setupListeners();
    new MutationObserver(() => this.checkRegionRestriction()).observe(
      this.querySelector("input[name='id']") || document.body,
      { attributes: true, attributeFilter: ["value"] }
    );

    // 6) region selector modal + initial check
    this.setupRegionSelectorModal();
    this.checkRegionRestriction();

    // 7) global hooks
    document.addEventListener("variant:changed", e => {
      if (this.contains(e.target)) this.checkRegionRestriction();
    });
    document.addEventListener("drawer:open", () => {
      if (this.closest("quick-buy-drawer")) this.checkRegionRestriction();
    });
    document.addEventListener("region:changed", e => {
      this.checkRegionRestriction(e.detail?.state);
    });
  }

  static get observedAttributes() {
    return ["variant-restricted-states"];
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === "variant-restricted-states" && oldVal !== newVal) {
      this.checkRegionRestriction();
    }
  }

  get messageSelector() {
    return this.getAttribute("message-selector") || null;
  }
  get restrictedMessage() {
    return this.getAttribute("restricted-message") ||
           "This product is not available in your selected region.";
  }
  get allowedMessage() {
    return this.getAttribute("allowed-message") ||
           "This product is available for purchase.";
  }
  get displayRestrictedMessage() {
    return this.getAttribute("display-restricted-message") === "true";
  }
  get displayAllowedMessage() {
    return this.getAttribute("display-allowed-message") === "true";
  }

  getCookie(name) {
    const m = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return m ? m[2] : null;
  }

  disableAddToCart() {
    this.addToCartButtons.forEach(b => {
      b.disabled = true;
      b.removeAttribute("aria-busy");
      b.style.opacity = "0.5";
    });
  }

  enableAddToCart() {
    this.addToCartButtons.forEach(b => {
      b.disabled = false;
      b.removeAttribute("aria-busy");
      b.style.opacity = "1";
    });
  }

  checkRegionRestriction(forcedRegion = null) {
// always clear any old message + visible flag
    this.removeAttribute('visible');
    this.messageContainer.innerHTML = '';
    const region = (forcedRegion || this.getCookie("customer_region") || "")
                   .toUpperCase();
    if (!this.addToCartButtons.length) return;

    // helper for “change region” link
    function makeRegionTrigger(region) {
      return `<a href="#"
                 class="inline-region-trigger"
                 data-region-trigger
                 aria-label="Choose shipping region">${region}</a>`;
    }

    const isProductPage = Boolean(this.getAttribute("variant-selector"));
    let isRestricted = false;

    if (isProductPage) {
      // PRODUCT PAGE: check only active variant
      const hid = this.querySelector("input[name='id']");
      const vid = hid?.value;
      const csv = (vid && this.variantRestrictionsMap[vid]) || "";
      const restrictedStates = csv
        .split(",")
        .map(s => s.trim().toUpperCase())
        .filter(Boolean);

      isRestricted =
        Boolean(region) &&
        restrictedStates.length > 0 &&
        restrictedStates.includes(region);

    } else {
      // COLLECTION PAGE: check all variants
      const lists = Object.values(this.variantRestrictionsMap)
        .filter(Boolean)
        .map(s => s.split(",").map(x => x.trim().toUpperCase()));

      isRestricted =
        Boolean(region) &&
        lists.length > 0 &&
        lists.every(arr => arr.includes(region));
    }

    // apply add-to-cart toggle & message
    if (isRestricted) {
      this.disableAddToCart();
      this.messageContainer.innerHTML = this.displayRestrictedMessage
        ? `${this.restrictedMessage} (${makeRegionTrigger(region)})`
        : "";
      if (this.displayRestrictedMessage) this.setAttribute("visible", "");
      window.triggerEvent?.(this, "region:restricted");
    } else {
      this.enableAddToCart();
      this.messageContainer.innerText = this.displayAllowedMessage
        ? this.allowedMessage
        : "";
      if (this.displayAllowedMessage) this.setAttribute("visible", "");
      window.triggerEvent?.(this, "region:allowed");
    }

    // hide entire card if requested
    const canHide = this.getAttribute("can-hide") === "yes";
    const shouldHide = canHide && this.hideCheckbox?.checked && isRestricted;
    const card = this.closest(this.getAttribute("product-card-selector")) ||
                 this.collectionProductCard ||
                 this;
    card.style.display = shouldHide ? "none" : "";
    card.classList.toggle("rr-hidden", shouldHide);
  }

  setupListeners() {
    const sel = this.getAttribute("variant-selector");
    if (!sel) return;

    // re-parse map
    try {
      this.variantRestrictionsMap = JSON.parse(
        this.getAttribute("data-variant-restrictions") || "{}"
      );
    } catch {
      this.variantRestrictionsMap = {};
    }

    // on each radio/select change
    this.querySelectorAll(sel).forEach(el => {
      el.addEventListener("change", () => {
        const nv = el.tagName === "SELECT"
          ? el.options[el.selectedIndex]?.getAttribute("data-restricted-states") || ""
          : el.getAttribute("data-restricted-states") || "";
        this.updateRestrictedStates(nv);
      });
    });

    // also on hidden <input name="id">
    const apply = () => {
      const hid = this.querySelector("input[name='id']");
      if (hid) {
        this.updateRestrictedStates(
          this.variantRestrictionsMap[hid.value] || ""
        );
      }
    };
    apply();
    document.addEventListener("variant:changed", e => {
      if (this.contains(e.target)) apply();
    });
  }

  updateRestrictedStates(val) {
    if (val) this.setAttribute("variant-restricted-states", val);
    else this.removeAttribute("variant-restricted-states");
    this.checkRegionRestriction();
  }

  setupRegionSelectorModal() {
    const modal = this.querySelector("#region-selector-modal");
    if (!modal) return;

    // OPEN
    this.addEventListener("click", e => {
      const trigger = e.target.closest("[data-region-selector-trigger]");
      if (!trigger) return;
      e.preventDefault();
      modal.style.display = "block";
    });

    // SAVE
    modal.addEventListener("click", e => {
      if (!e.target.closest("button.save-region")) return;
      const sel = modal.querySelector("select#region-selector");
      const st = sel && sel.value ? sel.value.toUpperCase() : "";
      if (!st) return;

      this.setCookie("customer_region", st, 7);
      localStorage.setItem("customer_region", st);
      if (typeof window.updateRegionRestrictions === "function") {
        window.updateRegionRestrictions(st);
      }
      modal.style.display = "none";
    });
  }
}

customElements.define("region-restriction", RegionRestriction);

// 🧠 Restore labels and cookies on load
document.addEventListener("DOMContentLoaded", () => {
  const stateMap = {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
    CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
    HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
    KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
    MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
    MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
    NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
    OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
    SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
    VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming"
  };

  function updateRegionLabel(id, stateCode) {
    const el = document.getElementById(id);
    if (!el || !/^[A-Z]{2}$/.test(stateCode)) return;
    el.textContent = stateCode;
    el.title = stateMap[stateCode] || "Choose shipping region";
  }

  let customerRegion = null;
  const match = document.cookie.match(/(?:^|;\s*)customer_region=([A-Z]{2})\b/i);
  if (match) {
    customerRegion = match[1].toUpperCase();
  } else if (localStorage.getItem("customer_region")) {
    customerRegion = localStorage.getItem("customer_region").toUpperCase();
    const date = new Date();
    date.setTime(date.getTime() + 7 * 86400000);
    document.cookie = `customer_region=${customerRegion};expires=${date.toUTCString()};path=/;`;
  }

  if (customerRegion) {
    window.updateRegionRestrictions(customerRegion);
    updateRegionLabel("region-label", customerRegion);
    updateRegionLabel("region-label-product", customerRegion);
    updateRegionLabel("region-label-product-mobile", customerRegion);
  }

  document.addEventListener("region:changed", e => {
    const newRegion = e.detail?.state?.toUpperCase();
    if (!newRegion) return;
    updateRegionLabel("region-label", newRegion);
    updateRegionLabel("region-label-product", newRegion);
    updateRegionLabel("region-label-product-mobile", newRegion);
  });
});

window.updateRegionRestrictions = st =>
  document.dispatchEvent(
    new CustomEvent("region:changed", { detail: { state: st } })
  );

// Sort products: available first, disabled last
function sortCollectionItems() {
  const wrappers = Array.from(document.querySelectorAll("region-restriction"));
  if (!wrappers.length) return;
  const container = wrappers[0].parentNode;
  const enabled = [];
  const disabled = [];
  wrappers.forEach(wrapper => {
    const isDisabled = !!wrapper.querySelector("button[disabled]");
    (isDisabled ? disabled : enabled).push(wrapper);
  });
  enabled.concat(disabled).forEach(el => container.appendChild(el));
}

document.addEventListener("DOMContentLoaded", sortCollectionItems);
document.addEventListener("region:changed",    sortCollectionItems);
